import { useEffect, useRef, useState } from "react";
import { api, apiErr, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, Save, Loader2, Upload, ImageIcon } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoOk, setLogoOk] = useState(true);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  useEffect(() => { setLogoOk(true); }, [logoBust]);

  const isAdmin = user?.role === "admin";

  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/settings", s);
      setS(data);
      toast.success("Company settings updated. New receipts and PDFs will use these values.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setBusy(false); }
  }

  async function uploadLogo(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please pick an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Logo must be under 5 MB");
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded. New PDFs will use this logo.");
      setLogoBust(Date.now());
    } catch (e) {
      toast.error(apiErr(e));
    } finally { setLogoBusy(false); }
  }

  if (!s) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-extrabold text-slate-900">Company Settings</h1>
        <p className="text-slate-500 mt-1">These details appear on every money receipt & offload PDF.</p>
      </div>

      <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
          <Building2 className="h-5 w-5 text-blue-600" />
          <h3 className="font-bold text-slate-800">Legal & Contact Details</h3>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Company Name">
            <input data-testid="s-company-name" disabled={!isAdmin} value={s.company_name || ""} onChange={(e) => setS({ ...s, company_name: e.target.value })} className={inputCls} />
          </Field>
          <Field label="Tagline">
            <input disabled={!isAdmin} value={s.tagline || ""} onChange={(e) => setS({ ...s, tagline: e.target.value })} className={inputCls} />
          </Field>

          <Field label="GSTIN" required>
            <input data-testid="s-gstin" disabled={!isAdmin} value={s.gstin || ""} onChange={(e) => setS({ ...s, gstin: e.target.value.toUpperCase() })} className={inputCls + " mono uppercase"} maxLength={15} />
          </Field>
          <Field label="PAN">
            <input disabled={!isAdmin} value={s.pan || ""} onChange={(e) => setS({ ...s, pan: e.target.value.toUpperCase() })} className={inputCls + " mono uppercase"} maxLength={10} />
          </Field>

          <div className="md:col-span-2">
            <Field label="Address Line 1" required>
              <input data-testid="s-addr1" disabled={!isAdmin} value={s.address_line1 || ""} onChange={(e) => setS({ ...s, address_line1: e.target.value })} className={inputCls} />
            </Field>
          </div>
          <div className="md:col-span-2">
            <Field label="Address Line 2 (City, State, PIN)" required>
              <input data-testid="s-addr2" disabled={!isAdmin} value={s.address_line2 || ""} onChange={(e) => setS({ ...s, address_line2: e.target.value })} className={inputCls} />
            </Field>
          </div>

          <Field label="State Code">
            <input disabled={!isAdmin} value={s.state_code || ""} onChange={(e) => setS({ ...s, state_code: e.target.value })} className={inputCls + " mono"} maxLength={2} />
          </Field>
          <Field label="Phone">
            <input disabled={!isAdmin} value={s.phone || ""} onChange={(e) => setS({ ...s, phone: e.target.value })} className={inputCls} placeholder="+91 98xxxxxxxx" />
          </Field>
          <Field label="Email">
            <input disabled={!isAdmin} value={s.email || ""} onChange={(e) => setS({ ...s, email: e.target.value })} className={inputCls} placeholder="info@ratanmotors.com" />
          </Field>
          <Field label="Website">
            <input disabled={!isAdmin} value={s.website || ""} onChange={(e) => setS({ ...s, website: e.target.value })} className={inputCls} placeholder="www.ratanmotors.com" />
          </Field>
        </div>

        {isAdmin ? (
          <button data-testid="s-save" disabled={busy} className="mt-2 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold text-sm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </button>
        ) : (
          <div className="text-xs text-slate-500 italic">Read-only. Ask an admin to update these details.</div>
        )}
      </form>

      {/* Logo Upload */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center gap-2 pb-3 border-b border-slate-100 mb-4">
          <ImageIcon className="h-5 w-5 text-blue-600" />
          <h3 className="font-bold text-slate-800">Company Logo</h3>
        </div>
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="h-28 w-28 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
            <img
              src={`${API_BASE}/settings/logo?v=${logoBust}`}
              alt="logo"
              className="h-full w-full object-contain p-2"
              onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.parentElement.innerHTML = '<div class="text-slate-400 text-xs text-center">No logo<br/>uploaded</div>'; }}
              data-testid="logo-preview"
            />
          </div>
          <div className="flex-1 space-y-2">
            <p className="text-sm text-slate-600">
              Upload your official Ratan Motors logo. It appears on every Money Receipt & Voucher PDF.
              PNG or JPG, up to 5 MB. Transparent PNG recommended.
            </p>
            {isAdmin ? (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  data-testid="logo-file-input"
                  onChange={(e) => uploadLogo(e.target.files?.[0])}
                />
                <button
                  type="button"
                  disabled={logoBusy}
                  data-testid="logo-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold text-sm"
                >
                  {logoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {logoBusy ? "Uploading…" : "Upload New Logo"}
                </button>
              </>
            ) : (
              <div className="text-xs text-slate-500 italic">Only admins can upload a logo.</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-sm text-blue-900">
        <div className="font-semibold mb-1">Where does this show?</div>
        <ul className="list-disc pl-5 space-y-0.5 text-blue-800">
          <li>Header of every Money Receipt PDF</li>
          <li>Header of every Bank Deposit / Owner Handover voucher PDF</li>
          <li>GSTIN + PAN print on the right side of the header for tax compliance</li>
        </ul>
      </div>
    </div>
  );
}

const inputCls = "w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-900 bg-white disabled:bg-slate-50 disabled:text-slate-500";
function Field({ label, required, children }) {
  return (<label className="block"><div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label} {required && <span className="text-red-500">*</span>}</div>{children}</label>);
}
